import React, { useEffect, useRef, useState } from "react";
import { Button } from "../../../design/button";
import {
  Dialog,
  DialogActions,
  DialogBody,
  DialogTitle,
} from "../../../design/dialog";
import { useDiscoveryRequestTaskResponse } from "../../../contexts/DiscoveryRequestTaskResponseContext";
import { DiscoveryRequestTask } from "../../../models/DiscoveryRequestTask";
import {
  ErrorMessage,
  Field,
  FieldGroup,
  Fieldset,
  Label,
} from "../../../design/fieldset";
import { Input } from "../../../design/input";
import { Textarea } from "../../../design/textarea";

interface Props {
  discoveryRequestTask: DiscoveryRequestTask;
  onCreate?: (newDiscoveryRequestTaskResponseId: string) => Promise<void>;
  isOpen: boolean;
  setIsOpen: React.Dispatch<React.SetStateAction<boolean>>;
}

interface Errors {
  title?: string;
  description?: string;
}

const CreateDiscoveryRequestTaskResponseDialog: React.FC<Props> = ({
  discoveryRequestTask,
  onCreate,
  isOpen,
  setIsOpen,
}) => {
  const { createDiscoveryRequestTaskResponse } =
    useDiscoveryRequestTaskResponse();
  const [isSaving, setIsSaving] = useState(false);
  const [file, setFile] = useState<File | null>(null);
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [errors, setErrors] = useState<Errors>({});

  useEffect(() => {
    if (file) {
      setTitle(file.name);
    }
  }, [file]);

  const validateForm = () => {
    const newErrors: Errors = {};
    if (!title.trim()) newErrors.title = "A title is required.";
    return newErrors;
  };

  const handleSubmit = async () => {
    try {
      const validationErrors = validateForm();
      if (Object.keys(validationErrors).length > 0) {
        setErrors(validationErrors);
        return;
      }

      setIsSaving(true);
      await createDiscoveryRequestTaskResponse(file, {
        discoveryRequestTask: discoveryRequestTask._id,
        title,
        description,
      });
      clearForm();
    } catch (error) {
      console.error("Error creating discovery request task response:", error);
      alert(
        "Failed to create discovery request task response. Please try again later.",
      );
    } finally {
      setIsSaving(false);
    }
  };

  const clearForm = () => {
    setTitle("");
    setDescription("");
    setFile(null);
    setIsSaving(false);
    setIsOpen(false);
  };

  return (
    <Dialog open={isOpen} onClose={setIsOpen}>
      <DialogTitle>Create Response</DialogTitle>
      <DialogBody>
        <Fieldset>
          <FieldGroup>
            <Field>
              <Label>File</Label>
              <Input
                type="file"
                accept="application/*"
                onChange={(e) => setFile(e.target.files?.[0] ?? null)}
              />
            </Field>

            <Field>
              <Label>Title</Label>
              <Input
                type="text"
                value={title}
                placeholder="Title"
                onChange={(e) => setTitle(e.target.value)}
              />
              {errors.title && <ErrorMessage>{errors.title}</ErrorMessage>}
            </Field>
            <Field>
              <Label>Description</Label>
              <Textarea
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="Description"
              />
            </Field>
          </FieldGroup>
        </Fieldset>
      </DialogBody>
      <DialogActions>
        <Button plain onClick={clearForm}>
          Cancel
        </Button>
        <Button disabled={isSaving} onClick={handleSubmit}>
          {isSaving ? "Saving..." : "Save"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default CreateDiscoveryRequestTaskResponseDialog;
